import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SearchBox,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MailSettings20Regular,
  Person20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { BrandWordmark, useBrand } from '@gandhi-group/ui';
import { useState, type ReactElement } from 'react';
import { activeMailbox } from '../state/mail-store.js';
import { useController, useMail } from '../state/context.js';
import { MailAppSetup } from './MailAppSetup.js';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr minmax(0, 2fr) 1fr',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    background: tokens.colorBrandBackground2,
  },
  identity: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    minWidth: 0,
    '@media (max-width: 900px)': { display: 'none' },
  },
  address: { color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  search: { justifySelf: 'center', width: '100%', maxWidth: '520px' },
  trailing: { justifySelf: 'end', display: 'flex', alignItems: 'center', gap: '4px' },
});

/**
 * One band across the top carrying the brand: accent rule, wordmark, and the address
 * everything sent next leaves from. Search sits in the middle, where it is reachable
 * from any folder rather than tied to one list.
 */
export interface CommandBarProps {
  /** Forget the password and close the mailboxes. */
  readonly onSignOut: () => void;
}

export function CommandBar({ onSignOut }: CommandBarProps): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const { accent } = useBrand();
  const mailbox = useMail(activeMailbox);
  const mode = useMail((s) => s.themeMode);
  const search = useMail((s) => s.search);
  const [term, setTerm] = useState(search);
  const [setupOpen, setSetupOpen] = useState(false);

  return (
    <header className={styles.root} style={{ borderTop: `6px solid ${accent}` }}>
      <div className={styles.identity}>
        <BrandWordmark />
        <Text className={styles.address} weight="semibold" truncate data-testid="active-mailbox">
          {mailbox?.address ?? 'No mailbox'}
        </Text>
      </div>

      <form
        className={styles.search}
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          void controller.setSearch(term.trim());
        }}
      >
        <SearchBox
          style={{ width: '100%' }}
          aria-label="Search this mailbox"
          placeholder="Search this mailbox"
          value={term}
          onChange={(_event, data) => {
            setTerm(data.value);
            if (data.value === '' && search !== '') void controller.setSearch('');
          }}
        />
      </form>

      <div className={styles.trailing}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<Person20Regular />}
              aria-label={`Signed in as ${mailbox?.address ?? 'nobody'}`}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem disabled>{mailbox?.address ?? 'No mailbox'}</MenuItem>
              <MenuDivider />
              {mailbox !== null && (
                <MenuItem icon={<MailSettings20Regular />} onClick={() => setSetupOpen(true)}>
                  Set up another mail app
                </MenuItem>
              )}
              {/* No identity provider to leave: this client authenticates to the mail
                  server directly, so signing out is forgetting the password it holds. */}
              <MenuItem icon={<SignOut20Regular />} onClick={onSignOut}>
                Sign out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <Tooltip
          content={mode === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          relationship="label"
        >
          <Button
            appearance="subtle"
            icon={mode === 'light' ? <WeatherMoon20Regular /> : <WeatherSunny20Regular />}
            aria-label={mode === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            onClick={() => controller.setThemeMode(mode === 'light' ? 'dark' : 'light')}
          />
        </Tooltip>
      </div>

      {mailbox !== null && (
        <MailAppSetup mailbox={mailbox} open={setupOpen} onOpenChange={setSetupOpen} />
      )}
    </header>
  );
}
